import { useState } from "react";
import { auth } from "../firebase";
import { createUser, sendActivationEmail } from "./staffApi";

/**
 * Create a new user for an organization
 */
export default function CreateUser({ organization, setCreatingStaff, onCreated }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "user",
    active: true
  });
  const [sendActivation, setSendActivation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }
    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }
    if (!isValidEmail(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setSubmitting(true);
    const email = formData.email.trim().toLowerCase();
    try {
      const token = await auth.currentUser.getIdToken();
      await createUser({
        token,
        organizationId: organization.id,
        name: formData.name.trim(),
        email,
        role: formData.role,
        active: formData.active,
      });
    } catch (e) {
      console.error("Error creating user:", e);
      setError(e.message || "Failed to create user");
      setSubmitting(false);
      return;
    }

    let notice = `Created ${email}.`;
    if (sendActivation) {
      try {
        await sendActivationEmail({ auth, email });
        notice = `Created ${email} and sent the activation email.`;
      } catch (e) {
        console.error("Error sending activation email:", e);
        notice = `Created ${email}, but the activation email failed to send. Use Send activation email from the list.`;
      }
    }
    onCreated?.(notice);
    setCreatingStaff(false);
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Create New {organization.type === 'vasi' ? 'Staff Member' : 'User'} - {organization.name}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Add a new {organization.type === 'vasi' ? 'staff member' : 'user'} to this organization.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
              Name *
            </label>
            <div className="mt-2">
              <input
                type="text"
                id="name"
                required
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
              Email *
            </label>
            <div className="mt-2">
              <input
                type="email"
                id="email"
                required
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium leading-6 text-gray-900">
              Role
            </label>
            <div className="mt-2">
              <select
                id="role"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
              </select>
            </div>
          </div>

          <div className="relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                id="active"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label htmlFor="active" className="font-medium text-gray-900">
                Active
              </label>
              <p className="text-gray-500">User can access the system when active</p>
            </div>
          </div>

          <div className="relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                id="send-activation"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                checked={sendActivation}
                onChange={(e) => setSendActivation(e.target.checked)}
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label htmlFor="send-activation" className="font-medium text-gray-900">
                Send activation email now
              </label>
              <p className="text-gray-500">
                The link expires, so leave this off if they won't set up their device right away.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-x-6">
          <button
            type="button"
            className="text-sm font-semibold leading-6 text-gray-900"
            onClick={() => setCreatingStaff(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-indigo-600 disabled:opacity-50 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            {submitting ? 'Creating...' : `Create ${organization.type === 'vasi' ? 'Staff Member' : 'User'}`}
          </button>
        </div>
      </form>
    </div>
  );
} 